import { TimeField, type TimeFieldProps } from '@/global/components/TimeField'
import {
    Controller,
    type UseControllerProps,
    useFormContext,
} from 'react-hook-form'

type FormTimeProps = Omit<UseControllerProps, 'control'> &
    Omit<TimeFieldProps, 'value' | 'name'> & {
        name: string
        defaultValue?: string
        hidden?: boolean
    }

/**
 * Campo de hora ligado ao react-hook-form — o `TimeField` dentro de um
 * `Controller`, no mesmo molde do `FormDatePicker`. Substitui o
 * `<FormField type="time">`, que desenhava o controle nativo do navegador.
 *
 * O valor gravado continua sendo a string `HH:mm` de sempre, e o `onChange` do
 * call-site recebe essa string — não um evento, como no `FormField`.
 */
export function FormFieldTime({
    name,
    rules,
    defaultValue,
    hidden,
    disabled: disabledCampo,
    onChange,
    className,
    error,
    ...props
}: FormTimeProps) {
    const { control } = useFormContext()
    // `_options` é API interna do RHF — daí o cast.
    const disabled = disabledCampo || (control as any)._options?.disabled

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({
                field: { value, onChange: aoMudar, onBlur },
                fieldState: { error: erroDoCampo },
            }) => (
                <TimeField
                    {...props}
                    name={name}
                    data-testid={name}
                    value={value ?? ''}
                    onChange={hora => {
                        aoMudar(hora)
                        onChange?.(hora)
                    }}
                    // O `onBlur` do RHF marca o campo como tocado; o do
                    // call-site, quando existe, vem depois (ver `TimeField`).
                    onBlur={evento => {
                        onBlur()
                        props.onBlur?.(evento)
                    }}
                    disabled={disabled}
                    className={hidden ? 'hidden!' : className}
                    error={
                        error ?? (erroDoCampo && (erroDoCampo.message ?? true))
                    }
                />
            )}
        />
    )
}
